import { useMemo, useState } from "react"
import { Check } from "lucide-react"
import type { CatalogItem } from "../types"
import { formatPrice, requiredPackageCount } from "../lib/format"
import type { SelectedPackage } from "../types"

interface Props {
  packages: CatalogItem[]
  /** Staffel-Produkte, um auf den guenstigeren Preis hinzuweisen. */
  staffel: CatalogItem[]
  /** Uebergibt die gewaehlten Pakete an den Warenkorb. */
  onAdd: (items: CatalogItem[]) => void
  /** Wechsel in den Staffelpreis, mit der bisherigen Auswahl. */
  onUpgrade: (staffelItem: CatalogItem, selected: SelectedPackage[]) => void
  /** Vorauswahl aus einem Deeplink (?produkt=infopaket-...). */
  preselect?: string | null
}

/**
 * Einzelbezug der Infopakete: zweispaltige Mehrfachauswahl mit laufender Summe.
 * Ersetzt den frueheren Popup-Browser - der Kunde sieht alle Pakete auf einen Blick.
 */
export function PackageGrid({ packages, staffel, onAdd, onUpgrade, preselect }: Props) {
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(preselect && packages.some((p) => p.lookup_key === preselect) ? [preselect] : []),
  )

  const chosen = useMemo(
    () => packages.filter((p) => p.lookup_key && picked.has(p.lookup_key)),
    [packages, picked],
  )
  const total = chosen.reduce((s, p) => s + (p.amount_net ?? 0), 0)

  // Ab zwei Paketen gibt es einen Staffelpreis. Ohne Hinweis wuerde der Kunde
  // im Einzelbezug mehr zahlen, ohne es zu merken.
  const cheaper = useMemo(() => {
    const hit = staffel.find((s) => requiredPackageCount(s) === chosen.length)
    if (!hit || hit.amount_net == null || hit.amount_net >= total) return null
    return { item: hit, saving: total - hit.amount_net }
  }, [staffel, chosen.length, total])

  function toggle(key: string) {
    setPicked((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  return (
    <div className="rounded-card border border-line bg-white p-5 shadow-card md:p-7">
      <div className="grid gap-3 md:grid-cols-2 md:gap-x-6">
        {packages.map((p) => {
          const key = p.lookup_key ?? p.price_id
          const on = picked.has(key)
          return (
            <button
              key={p.price_id}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(key)}
              className={`flex w-full items-center gap-3 rounded-field border px-4 py-3 text-left transition ${
                on ? "border-brand-blue bg-brand-tint" : "border-line hover:border-brand-blue"
              }`}
            >
              <span
                className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-pick border ${
                  on ? "border-brand-blue bg-brand-blue" : "border-line bg-white"
                }`}
              >
                {on && <Check className="h-3 w-3 text-white" strokeWidth={3.2} />}
              </span>
              <span className="flex-1 text-sm">{p.name}</span>
              <span className="text-sm text-brand-muted">{formatPrice(p.amount_net, p.currency)}</span>
            </button>
          )
        })}
      </div>

      {cheaper && (
        <div className="mt-6 flex flex-col gap-3 rounded-field border border-brand-blue bg-brand-tint p-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm">
            Mit dem Staffelpreis für {chosen.length} Pakete zahlen Sie{" "}
            <span className="font-medium">{formatPrice(cheaper.item.amount_net)}</span> statt{" "}
            {formatPrice(total)} – Sie sparen {formatPrice(cheaper.saving)}.
          </p>
          <button
            onClick={() => onUpgrade(cheaper.item, chosen.map((p) => ({ package_id: p.package_id ?? "", name: p.name })))}
            className="shrink-0 rounded-field bg-brand-blue px-6 py-2.5 text-sm font-medium text-white transition hover:brightness-105"
          >
            Staffelpreis wählen
          </button>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-4 border-t border-line pt-5 md:flex-row md:items-center md:justify-between">
        <span className="flex items-center gap-2 text-sm text-brand-muted">
          <Check className="h-4 w-4 shrink-0" strokeWidth={2.4} />
          Dauerhafter Zugang über das SINUS Infoportal
        </span>

        <div className="flex flex-wrap items-center justify-between gap-3 md:justify-end">
          <span className="text-sm text-brand-muted">
            {chosen.length === 1 ? "1 Paket ausgewählt" : `${chosen.length} Pakete ausgewählt`}
          </span>
          <span className="text-lg font-medium text-brand-blue md:text-2xl">{formatPrice(total)}</span>
          <button
            disabled={chosen.length === 0}
            onClick={() => {
              onAdd(chosen)
              setPicked(new Set())
            }}
            className="w-full rounded-field bg-brand-blue px-6 py-2.5 text-sm font-medium text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45 md:w-auto"
          >
            Auswahl in den Warenkorb
          </button>
        </div>
      </div>
    </div>
  )
}
